import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { SelectorData } from 'src/app/utils/selectorDataType';
import { ProductLabels } from '../add-edit-product/add-edit-product.component';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs';
import { ProductService } from 'src/app/core/api/product/product.service';
import { ProductDTO } from 'src/app/shared/models/dtos/responses/product-dto/product-dto';
import { MatSelectChange } from '@angular/material/select';

@Component({
  selector: 'app-product-form',
  templateUrl: './product-form.component.html',
  styleUrls: ['./product-form.component.scss'],
})
/**
 * Form component for produt addition and edition
 */
export class ProductFormComponent implements OnInit {
  @Input() form!: FormGroup;
  @Input() product!: ProductDTO | undefined;
  @Input() labels!: ProductLabels;
  @Input() productFamilies!: SelectorData[];
  @Output() isEditing = new EventEmitter<boolean>();

  headerInputControl = new FormControl<string>('', [
    Validators.maxLength(4),
    Validators.minLength(4),
  ]);

  steps = [{ label: 'Product Details' }, { label: 'Review' }];
  selectedFamily: string = '';
  currentStep = 0;

  /**
   * Constructor for ProductFormComponent.
   */
  constructor(private productService: ProductService) {}

  /**
   * Initializer
   * Subscribes to the 'name' control value changes to validate its availability.
   */
  ngOnInit(): void {
    this.getControl('name').valueChanges.subscribe((value) => {
      this.isEditing.emit(true);

      if (this.product && value === this.product.name) {
        this.isEditing.emit(false);
      }
    });

    this.getControl('name')
      .valueChanges.pipe(
        debounceTime(500),
        distinctUntilChanged(),
        filter(() => this.getControl('name').valid)
      )
      .subscribe((value) => {
        const control = this.getControl('name');
        if (this.product && value === this.product.name) {
          this.selectedFamily = this.product.family;
          return this.isEditing.emit(false);
        }
        this.productService
          .validateProductByName(value)
          .subscribe((response) => {
            this.isEditing.emit(false);
            control.setErrors(
              response.available
                ? null
                : { custom: 'product-name-already-in-use' }
            );
          });
      });
  }

  /**
   * Handles the selection of a printer series option.
   * Updates the selected family and form control value.
   * @param {MatSelectChange} option - The selected printer series.
   */
  onOptionSelected(option: MatSelectChange) {
    this.selectedFamily = option.value;
    this.form.controls['family'].setValue(option.value);
    this.form.controls['family'].setValue(option.value);
    this.form.controls['familyId'].setValue(
      this.productFamilies.find((f) => f.value === option.value)?.id
    );
  }

  /**
   * Get the current product family selected
   * @return {SelectorData} the selected product family
   */
  getFamilyOptionSelected() {
    const selectedFamily = this.product
      ? this.product?.family
      : this.form.controls['family'].value;

    return this.productFamilies.find((f) => f.value === selectedFamily);
  }

  /**
   * Handles the key event for the header input field.
   * Validates and adds new headers to the form control if the input is valid.
   * @param {KeyboardEvent} event - The keyboard event triggered by user input.
   */
  onHeaderKey(event: KeyboardEvent) {
    const value = this.headerInputControl.value?.trim().toUpperCase() || '';
    if (event.key === 'Enter' && value) {
      event.preventDefault();

      const parts = value
        .split(';')
        .map((header) => header.trim().toUpperCase());

      const currentHeaders = (this.getControl('headers').value ||
        []) as string[];

      const invalid = parts.find((header) => !/^[A-Z0-9]{4}$/.test(header));
      if (invalid) {
        this.headerInputControl.setErrors({
          custom: 'product-header-must-have-4-chars',
        });
        this.headerInputControl.markAsTouched();
        return;
      }

      const alreadyExists = parts.some((header) =>
        currentHeaders.includes(header)
      );
      if (alreadyExists) {
        this.headerInputControl.setErrors({
          custom: 'product-headers-already-in-use',
        });
        this.headerInputControl.markAsTouched();
        return;
      }

      this.productService.validateProductByHeader(parts).subscribe({
        next: (response) => {
          if (response.alreadyInUse.length > 0) {
            this.headerInputControl.setErrors({
              custom: 'product-headers-already-in-use',
            });
            this.headerInputControl.markAsTouched();
            return;
          }

          this.getControl('headers').setValue([...currentHeaders, ...parts]);
          this.headerInputControl.setValue('');
        },
        error: () => {
          this.headerInputControl.setErrors({
            custom: 'product-header-validation-error',
          });
          this.headerInputControl.markAsTouched();
        },
      });
    }
  }

  /**
   * Removes a header from the headers form control.
   * @param {string} header - The header to be removed.
   */
  removeHeader(header: string) {
    const current = (this.getControl('headers').value || []) as string[];
    const updated = current.filter((h) => h !== header);
    this.form.patchValue({ headers: updated });
  }

  /**
   * Retrieves a specific form control by its name.
   * @param {string} controlName - The name of the control to retrieve.
   * @return {FormControl} The requested form control.
   */
  getControl(controlName: string): FormControl {
    return this.form.get(controlName) as FormControl;
  }
}
